import React, { Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import HeroSection from './components/HeroSection/HeroSection';
import { fetchCategories } from './api/fetchCategories';
import { useDispatch, useSelector } from 'react-redux';
import { loadCategories } from './store/features/category';
import { setLoading } from './store/features/common';
import { Link } from 'react-router-dom';
import { lazyWithRetry } from './lazyWithRetry';

/**
 * Homepage — eager loads only the LCP-critical content:
 *   • HeroSection (above the fold, contains the LCP image)
 *
 * Everything below the fold is code-split into its own chunk and
 * mounted via React.lazy + Suspense. The first paint only needs the
 * hero JS + CSS, which trims the homepage's critical JS by roughly
 * 20-40 KB (NewArrivals + RopasHomeSection + Newsletter combined).
 *
 * lazyWithRetry handles ChunkLoadError on stale-cache deploys.
 */
const NewArrivals = lazyWithRetry(() => import('./components/Sections/NewArrivals'), 'NewArrivals');
const RopasHomeSection = lazyWithRetry(
  () => import('./components/Sections/Categories/RopasHomeSection'),
  'RopasHomeSection'
);
const Newsletter = lazyWithRetry(() => import('./components/Sections/Newsletter'), 'Newsletter');

/* ── Editorial divider ────────────────────────────────────────────── */
const EditorialDivider = () => (
  <div className="max-w-container mx-auto px-6 md:px-10">
    <div className="flex items-center gap-6 py-4">
      <div className="flex-1 h-px bg-line-soft" />
      <p
        className="text-[10px] tracking-[0.25em] uppercase text-ink-tertiary whitespace-nowrap select-none"
        aria-hidden="true"
      >
        Pinksisly · Buenos Aires
      </p>
      <div className="flex-1 h-px bg-line-soft" />
    </div>
  </div>
);

/* ── Brand statement ──────────────────────────────────────────────── */
const BrandStatement = () => (
  <section className="bg-ink-primary text-white">
    <div className="max-w-container mx-auto px-6 md:px-10 py-16 md:py-24 lg:py-32">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-end">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-white/40 mb-6">
            Nuestra filosofía
          </p>
          <h2 className="font-display italic text-[38px] sm:text-[50px] lg:text-[64px] leading-[1.0] text-white mb-0">
            Moda que habla
            <br />
            de quién sos.
          </h2>
        </div>
        <div className="flex flex-col gap-6 lg:pb-2">
          <p className="text-[15px] text-white/60 leading-relaxed">
            Cada prenda está elegida para acompañarte en el día a día.
            <br className="hidden sm:block" />
            Estilo sin esfuerzo, calidad que se siente.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/ropas"
              className="inline-flex items-center justify-center h-12 px-8 bg-white text-ink-primary text-[11px] tracking-[0.15em] uppercase hover:bg-accent-soft transition-colors duration-300"
            >
              Comprar ahora
            </Link>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center h-12 px-8 border border-white/25 text-white/80 text-[11px] tracking-[0.15em] uppercase hover:border-white/60 hover:text-white transition-colors duration-300"
            >
              Contacto
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Skeleton placeholders sized to the real sections.
   Reserving height prevents CLS while the lazy chunk loads. */
const SectionSkeleton = ({ minHeight = 360 }) => (
  <div aria-hidden="true" style={{ minHeight }} className="w-full" />
);

const Shop = () => {
  const dispatch = useDispatch();
  const categoryData = useSelector((state) => state?.categoryState?.categories);

  useEffect(() => {
    // Skip if categories are already in the store (e.g. navigating back from /ropas)
    if (categoryData?.length) return;
    fetchCategories()
      .then((res) => {
        dispatch(loadCategories(res));
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  }, [dispatch, categoryData?.length]);

  return (
    <>
      <Helmet>
        <title>Pinksisly | Moda Femenina Argentina</title>
        <meta
          name="description"
          content="Pinksisly — Moda femenina importada de tendencia. Ropa con envíos a todo el país desde Buenos Aires."
        />
        <meta property="og:title" content="Pinksisly | Moda Femenina Argentina" />
        <meta
          property="og:description"
          content="Moda femenina importada. Ropa de tendencia con envíos a todo el país."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Eager — LCP critical */}
      <HeroSection />

      {/* Below-the-fold — each section is its own chunk. We deliberately
          do NOT wrap them all in a single <Suspense>: that would block
          the whole stripe on the slowest chunk. Independent boundaries
          let each section paint as soon as it's ready. */}
      <Suspense fallback={<SectionSkeleton minHeight={720} />}>
        <NewArrivals />
      </Suspense>

      <EditorialDivider />

      <Suspense fallback={<SectionSkeleton minHeight={800} />}>
        <RopasHomeSection />
      </Suspense>

      <BrandStatement />

      <Suspense fallback={<SectionSkeleton minHeight={320} />}>
        <Newsletter />
      </Suspense>
    </>
  );
};

export default Shop;
